"use client";

import Button from "@/lib/components/Button/Button";
import { InputField } from "@/lib/components/Input/Input";
import { PhoneInput } from "@/lib/components/Input/PhoneInput";
import theme from "@/themes/velys-theme";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import styled, { keyframes, useTheme } from "styled-components";
import {
  CognitoIdentityProviderClient,
  ChangePasswordCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import config from "@/config";
import { useAuth } from "react-oidc-context";
import Image from "next/image";
import EditIcon from "@/assets/icons/EditIcon.svg";
import PersonalIcon from "@/assets/icons/profile/PersonalIcon.svg";
import PasswordIcon from "@/assets/icons/profile/PasswordIcon.svg";
import NotifPrefIcon from "@/assets/icons/profile/NotifPrefIcon.svg";
import AlertPrefIcon from "@/assets/icons/profile/AlertPrefIcon.svg";
import WarningIcon from "@/assets/icons/WarningIcon.svg";
import PasskeyIcon from "@/assets/icons/profile/PasskeyIcon.svg";
import TrashIcon from "@/assets/icons/DeleteIcon.svg";
import AddIcon from "@/assets/icons/AddIcon.svg";
import EmailIcon from "@/assets/icons/EmailIcon.svg";
import SmsIcon from "@/assets/icons/SmsIcon.svg";
import { Icon } from "../Icon/Icon";
import {
  formatPhoneNumber,
  hasAcceptedCountryCode,
  isValidPhoneNumber,
  parsePhoneNumberWithLocale,
} from "@/lib/helpers";
import { countryCallingCodes, ILocale } from "@/lib/constants/locale";
import { useUserProfile } from "@/lib/hooks/useUser";
import {
  useGetPatientById,
  usePatientPreferences,
  useUpdatePatientPreferences,
} from "@/lib/hooks/usePatient";
import { useCognitoApi } from "@/lib/hooks/useCognitoApi";
import { useParams } from "next/navigation";
import { useAuthStore } from "@/lib/stores/useAuthStore";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { DeletePasskeyContent } from "@/lib/components/Modal/DeletePasskeyContent";
import WarningModal from "@/lib/components/Modal/WarningModal";
import {
  NotificationTypePreference,
  PatientPreferencesInput,
} from "@/lib/dataAccess/appsync";

interface PatientProfileContentProps {
  handleVerifyPhoneNumber: () => void;
}

const Profile = styled.div`
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ProfileInfo = styled.div<{ $disabled?: boolean }>`
  border: 1px solid ${(props) => props.theme.cardBorder};
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 20px;
  opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};

  .header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 10px;

    .title {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    h3 {
      font-size: 1.25rem;
      font-family: var(--font-johnson-display), sans-serif;
    }

    @media (max-width: 400px) {
      flex-wrap: wrap;
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 20px;

    @media (max-width: 450px) {
      grid-template-columns: 1fr;
    }
  }

  .full-width {
    grid-column: 1 / -1;
  }

  i {
    font-size: 0.825rem;
    color: ${(props) => props.theme.black};
  }
`;

const InfoDetail = styled.div`
  label {
    color: ${(props) => props.theme.main};
    font-size: 0.8rem;
    font-family: var(--font-johnson-display), sans-serif;
    font-weight: bold;
    display: block;
    margin-bottom: 5px;
  }

  p {
    word-break: break-word;
  }
`;

const PhoneWarning = styled.p`
  color: ${(props) => props.theme.main};
  font-size: 0.8rem;
  font-weight: bold;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;

  @media (max-width: 400px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const PasskeysSection = styled.div`
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid ${(props) => props.theme.cardBorder};

  h4 {
    font-size: 1rem;
    font-family: var(--font-johnson-display), sans-serif;
    margin-bottom: 5px;
  }
`;

const PasskeysList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0;
`;

const PasskeyItem = styled.div`
  border: 1px solid ${(props) => props.theme.cardBorder};
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .passkey-info {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;

    .title {
      font-weight: bold;
      font-size: 0.95rem;
      word-break: break-word;
    }

    .description {
      font-size: 0.8rem;
    }
  }
`;

const PasskeyDeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 25px;
  height: 25px;
  border-radius: 5px;
  border: 1px solid ${(props) => props.theme.invalidPhone};
  background: ${(props) => props.theme.white};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${(props) => props.theme.invalidPhone}1a;
  }
`;

const NotifAlerts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 15px;
`;

const SmsDisabledWarning = styled.h5`
  font-weight: bold;
  font-size: 0.725rem;
  margin-top: 5px;

  span {
    text-transform: uppercase;
    color: ${(props) => props.theme.main};
  }
`;

const PreferenceItem = styled.div`
  border: 1px solid ${(props) => props.theme.cardBorder};
  border-radius: 5px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .title {
    color: ${(props) => props.theme.black};
    font-weight: bold;
    font-size: 1rem;
  }

  .description {
    font-size: 0.825rem;
  }
`;

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const Skeleton = styled.div<{ width?: string; height?: string }>`
  display: inline-block;
  height: ${({ height }) => height || "18px"};
  width: ${({ width }) => width || "80px"};
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: ${pulse} 1.4s ease infinite;
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    gap: 6px;

    button {
      padding: 3px 8px;
    }

    p {
      font-size: 0.8rem;
    }
  }

  @media (max-width: 400px) {
    flex-direction: column;
    gap: 4px;

    button {
      padding: 2px 6px;
    }

    p {
      font-size: 0.7rem;
    }
  }
`;

const initialProfile = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  gender: "",
  existing_password: "",
  password: "",
  confirm_password: "",
};

export default function PatientProfileContent({
  handleVerifyPhoneNumber,
}: PatientProfileContentProps) {
  const tToast = useTranslations("toast");
  const tPatientProfile = useTranslations("patientProfile");
  const tComponentText = useTranslations("componentText");
  const tProfile = useTranslations("profilePage");
  const auth = useAuth();
  const styledTheme = useTheme();
  const { locale } = useParams();
  const [profile, setProfile] = useState(initialProfile);
  const [isEditLogin, setIsEditLogin] = useState(false);
  const [isEditProfile, setIsEditProfile] = useState(false);
  const [editCountryCode, setEditCountryCode] = useState<string>(
    countryCallingCodes["en-US"]
  );
  const [isPasskeySupported, setIsPasskeySupported] = useState(false);
  const [passkeyToDelete, setPasskeyToDelete] = useState<string | null>(null);
  const user_profile = useAuthStore((state) => state.user_profile);
  const userId = user_profile?.sub;
  const patientQuery = useGetPatientById(userId);

  // expose edit state so the locale dropdown in the modal header can be disabled
  const setIsEditingProfile = useBoundStore(
    (state) => state.setIsEditingProfile
  );

  const initialProfileRef = useRef(initialProfile);
  const { userProfileQuery } = useUserProfile();
  const {
    updatePhoneMutation,
    listPasskeysQuery,
    redirectToAddPasskey,
    deletePasskeyMutation,
  } = useCognitoApi();

  // notification preferences (VELYS-518) — self-service only, no userId arg
  const patientPreferencesQuery = usePatientPreferences();
  const initialPreferencesRef = useRef<PatientPreferencesInput>({});
  const [editedPreferences, setEditedPreferences] =
    useState<PatientPreferencesInput>({});
  const updatePatientPreferencesMutation = useUpdatePatientPreferences();

  const canReceiveSms =
    !!userProfileQuery.data?.phone &&
    !!userProfileQuery.data?.phone_number_verified;

  const notificationCategories: {
    key: keyof PatientPreferencesInput;
    label: string;
    description: string;
  }[] = [
    {
      key: "careplanReminders",
      label: tPatientProfile("careplanReminders"),
      description: tPatientProfile("careplanRemindersDescription"),
    },
    {
      key: "actionItemStatusChange",
      label: tPatientProfile("actionItemStatusChange"),
      description: tPatientProfile("actionItemStatusChangeDescription"),
    },
    {
      key: "enrollmentUpdated",
      label: tPatientProfile("enrollmentUpdated"),
      description: tPatientProfile("enrollmentUpdatedDescription"),
    },
    {
      key: "enrollmentCancelled",
      label: tPatientProfile("enrollmentCancelled"),
      description: tPatientProfile("enrollmentCancelledDescription"),
    },
    {
      key: "photoResponse",
      label: tPatientProfile("photoResponse"),
      description: tPatientProfile("photoResponseDescription"),
    },
  ];

  const notificationsChanged =
    JSON.stringify(initialPreferencesRef.current) !==
    JSON.stringify(editedPreferences);

  const client = new CognitoIdentityProviderClient({
    region: config.cognito.region || "",
  });

  const localeCountryCode = locale
    ? countryCallingCodes[locale as ILocale]
    : countryCallingCodes["en-US"];

  const { countryCode, locale: storedLocale } = parsePhoneNumberWithLocale(
    userProfileQuery.data?.phone || null
  );

  const displayCountryCode = countryCode ?? localeCountryCode;

  const getGenderLabel = (gender: string) => {
    if (!gender || gender === "") return "";
    if (gender === "M" || gender === "male" || gender === "Male") {
      return tComponentText("male");
    } else if (gender === "F" || gender === "female" || gender === "Female") {
      return tComponentText("female");
    }
    return tComponentText("other");
  };

  useEffect(() => {
    if (!userProfileQuery.data || !patientQuery.data) return;

    let phoneNumber = userProfileQuery.data?.phone || "";

    // Strip country code if present
    if (phoneNumber) {
      const allCountryCodes = Object.values(countryCallingCodes);
      const sortedCodes = allCountryCodes.sort((a, b) => b.length - a.length);

      for (const code of sortedCodes) {
        const regex = new RegExp(`^\\+?${code}`);
        if (regex.test(phoneNumber)) {
          phoneNumber = phoneNumber.replace(regex, "").trim();
          break;
        }
      }
    }

    const currentProfile = {
      first_name: userProfileQuery.data?.first_name || "",
      last_name: userProfileQuery.data?.last_name || "",
      email: userProfileQuery.data?.email || "",
      gender:
        userProfileQuery.data?.gender ||
        getGenderLabel(patientQuery.data.gender || "") ||
        "",
      phone_number: phoneNumber,
      existing_password: "",
      password: "",
      confirm_password: "",
    };

    setProfile(currentProfile);
    initialProfileRef.current = currentProfile;
  }, [userProfileQuery.data, patientQuery.data]);

  useEffect(() => {
    setIsEditingProfile(isEditProfile || isEditLogin);
    return () => setIsEditingProfile(false);
  }, [isEditProfile, isEditLogin]);

  useEffect(() => {
    const data = patientPreferencesQuery.data?.getPatientPreferences;
    if (!data) return;
    const { userId: _userId, ...preferences } = data;
    setEditedPreferences(preferences);
    initialPreferencesRef.current = preferences;
  }, [patientPreferencesQuery.data]);

  // Detect passkey / WebAuthn support in this browser
  useEffect(() => {
    setIsPasskeySupported(
      typeof window !== "undefined" && !!window.PublicKeyCredential
    );
  }, []);

  const handleChange = (field: string, value: string) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**** Profile ****/
  const handleEditProfile = () => {
    setEditCountryCode(displayCountryCode);
    setIsEditProfile(true);
  };

  const handleCancelEditProfile = () => {
    setProfile(initialProfileRef.current);
    setEditCountryCode(displayCountryCode);
    setIsEditProfile(false);
  };

  const handleSaveProfile = () => {
    const formattedPhone = `+${editCountryCode}${profile.phone_number.replace(/\D/g, "")}`;
    updatePhoneMutation.mutate(formattedPhone, {
      onSuccess: () => {
        toast.success(tProfile("phoneUpdateSuccess"));
        setIsEditProfile(false);
      },
      onError: () => {
        toast.error(tProfile("phoneUpdateFailed"));
      },
    });
  };

  /**** Login ****/
  const handleSaveEdit = async () => {
    try {
      const accessToken = auth.user?.access_token;

      const passwordChange = new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: profile.existing_password,
        ProposedPassword: profile.password,
      });

      await client.send(passwordChange);
      toast(tToast("profile.changed"));
      setProfile((prev) => ({
        ...prev,
        existing_password: "",
        password: "",
        confirm_password: "",
      }));
      setIsEditLogin(false);
    } catch (error) {
      toast(`${tToast("profile.error")}: ${error}`);
    }
  };

  const handleEditLogin = () => {
    setIsEditLogin(true);
  };

  const handleCancelEditLogin = () => {
    setProfile(initialProfileRef.current);
    setIsEditLogin(false);
  };

  const handleAddPasskey = () => {
    redirectToAddPasskey({ reopenPatientModal: true });
  };

  const handleDeletePasskey = (credentialId: string) => {
    setPasskeyToDelete(credentialId);
  };

  const confirmDeletePasskey = () => {
    if (!passkeyToDelete) return;
    deletePasskeyMutation.mutate(passkeyToDelete, {
      onSuccess: () => {
        toast.success(tProfile("passkeyDeleteSuccess"));
        setPasskeyToDelete(null);
      },
      onError: () => {
        toast.error(tProfile("passkeyDeleteFailed"));
      },
    });
  };

  /**** Notifications ****/
  const handleNotificationToggle = (
    category: keyof PatientPreferencesInput,
    channel: "email" | "sms",
    value: boolean
  ) => {
    setEditedPreferences((prev) => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [channel]: value,
      },
    }));
  };

  const handleSaveNotifs = () => {
    updatePatientPreferencesMutation.mutate(editedPreferences, {
      onSuccess: () => {
        toast(tToast("profile.saved"));
      },
      onError: () => {
        toast.error(tToast("profile.error"));
      },
    });
  };

  const handleCancelEditNotifs = () => {
    setEditedPreferences(initialPreferencesRef.current);
  };

  return (
    <>
      <Profile>
        {/* Personal Info */}
        <ProfileInfo $disabled={isEditLogin}>
          <div className="header-row">
            <div className="title">
              <Image
                src={PersonalIcon.src}
                height={25}
                width={25}
                alt="personal-info"
              />
              <h3>{tPatientProfile("section1")}</h3>
            </div>
            {!isEditProfile && (
              <Button
                label={tComponentText("edit")}
                name="edit-profile"
                theme="primary"
                onPress={handleEditProfile}
                icon={EditIcon.src}
                small
                isDisabled={isEditLogin}
              />
            )}
          </div>

          <div className="info-grid">
            <InfoDetail>
              <label>{tComponentText("name")}</label>
              <p>{`${profile.first_name} ${profile.last_name}`}</p>
            </InfoDetail>

            <InfoDetail>
              <label>{tComponentText("email")}</label>
              <p>{profile.email}</p>
            </InfoDetail>

            <InfoDetail>
              <label>{tComponentText("gender")}</label>
              <p>{profile.gender || tComponentText("NA")}</p>
            </InfoDetail>

            <div className="full-width">
              {!isEditProfile ? (
                <>
                  {hasAcceptedCountryCode(userProfileQuery.data?.phone) &&
                  !userProfileQuery.data?.phone_number_verified ? (
                    <div>
                      <InfoDetail
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div>
                          <label>{tComponentText("phoneNumber")}</label>
                          <p>
                            {profile.phone_number
                              ? `+${displayCountryCode} ${formatPhoneNumber(profile.phone_number, storedLocale ?? "en-US")}`
                              : tComponentText("NA")}
                          </p>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <Icon
                            url={WarningIcon.src}
                            color={theme.main}
                            height="25px"
                            width="25px"
                          />
                          <p style={{ fontSize: "0.8rem", color: theme.main }}>
                            {tComponentText("unverified")}
                          </p>
                        </div>
                      </InfoDetail>
                      <Button
                        label={tComponentText("verifyPhoneNumber")}
                        name="verify-phone-number"
                        theme="primary"
                        onPress={handleVerifyPhoneNumber}
                        styles={{ width: "fit-content", marginTop: 10 }}
                        small
                      />
                    </div>
                  ) : (
                    <InfoDetail>
                      <label>{tComponentText("phoneNumber")}</label>
                      <p>
                        {profile.phone_number
                          ? `+${displayCountryCode} ${formatPhoneNumber(profile.phone_number, storedLocale ?? "en-US")}`
                          : tComponentText("NA")}
                      </p>
                    </InfoDetail>
                  )}
                </>
              ) : (
                <div style={{ marginTop: 15 }}>
                  <PhoneInput
                    styles={{ minWidth: 0 }}
                    showNA={false}
                    countryCode={editCountryCode}
                    onCountryCodeChange={(code) => setEditCountryCode(code)}
                    value={
                      formatPhoneNumber(
                        profile.phone_number,
                        storedLocale ?? (locale as ILocale)
                      ) || ""
                    }
                    inputTitle={tComponentText("phoneNumber")}
                    onChange={(e) => {
                      const formattedNumber = formatPhoneNumber(
                        e.target.value,
                        locale as ILocale
                      );
                      handleChange("phone_number", formattedNumber);
                    }}
                    onClear={
                      profile.phone_number !== "" &&
                      !isValidPhoneNumber(profile.phone_number)
                        ? () => handleChange("phone_number", "")
                        : undefined
                    }
                    disabled={updatePhoneMutation.isPending}
                    color={styledTheme.main}
                    backgroundColor={
                      profile.phone_number !== "" &&
                      !isValidPhoneNumber(profile.phone_number)
                        ? styledTheme.invalidPhone
                        : styledTheme.white
                    }
                  />
                  {profile.phone_number !== "" &&
                    !isValidPhoneNumber(profile.phone_number) && (
                      <PhoneWarning>
                        {tProfile("phoneValidationWarning")}
                      </PhoneWarning>
                    )}
                </div>
              )}
            </div>
          </div>

          {isEditProfile && (
            <ButtonRow>
              <Button
                label={tComponentText("cancel")}
                name="cancel"
                theme="tertiary"
                onPress={handleCancelEditProfile}
                styles={{ width: "fit-content" }}
                isDisabled={updatePhoneMutation.isPending}
              />
              <Button
                label={tComponentText("saveChanges")}
                name="save-profile"
                theme="primary"
                onPress={handleSaveProfile}
                isDisabled={
                  !isValidPhoneNumber(profile.phone_number) ||
                  updatePhoneMutation.isPending
                }
                icon={updatePhoneMutation.isPending ? "loader" : undefined}
                iconColor={
                  updatePhoneMutation.isPending ? styledTheme.white : undefined
                }
              />
            </ButtonRow>
          )}
        </ProfileInfo>

        {/* Login Info */}
        <ProfileInfo $disabled={isEditProfile} style={{ minHeight: "221px" }}>
          <div className="header-row">
            <div className="title">
              <Image
                src={PasswordIcon.src}
                height={25}
                width={25}
                alt="password-info"
              />
              <h3>{tPatientProfile("section2")}</h3>
            </div>
            {!isEditLogin && (
              <Button
                label={tComponentText("edit")}
                name="edit-login"
                theme="primary"
                onPress={handleEditLogin}
                icon={EditIcon.src}
                small
                isDisabled={isEditProfile}
              />
            )}
          </div>

          <div className="info-grid">
            <InputField
              styles={{ minWidth: 0, maxWidth: "unset" }}
              type="password"
              value={
                !isEditLogin ? "************" : profile.existing_password || ""
              }
              inputTitle={tComponentText("existingPassword")}
              onChange={(e) =>
                handleChange("existing_password", e.target.value)
              }
              disabled={false}
              color={theme.main}
              backgroundColor={theme.white}
              readOnly={!isEditLogin}
            />
          </div>

          {isEditLogin ? (
            <>
              <div className="info-grid">
                <InputField
                  styles={{ minWidth: 0, maxWidth: "unset" }}
                  type="password"
                  value={profile.password || ""}
                  inputTitle={tComponentText("newPassword")}
                  onChange={(e) => handleChange("password", e.target.value)}
                  disabled={false}
                  color={theme.main}
                  backgroundColor={theme.white}
                />
                <InputField
                  styles={{ minWidth: 0, maxWidth: "unset" }}
                  type="password"
                  value={profile.confirm_password || ""}
                  inputTitle={tComponentText("confirmNewPassword")}
                  onChange={(e) =>
                    handleChange("confirm_password", e.target.value)
                  }
                  disabled={false}
                  color={theme.main}
                  backgroundColor={theme.white}
                />
              </div>

              <ButtonRow>
                <Button
                  label={tComponentText("cancel")}
                  name="cancel"
                  theme="tertiary"
                  onPress={handleCancelEditLogin}
                  styles={{ width: "fit-content" }}
                />
                <Button
                  label={tComponentText("changePassword")}
                  name="change-password"
                  theme="primary"
                  onPress={handleSaveEdit}
                  isDisabled={
                    profile.password === "" ||
                    profile.confirm_password === "" ||
                    profile.password !== profile.confirm_password ||
                    profile.existing_password === ""
                  }
                />
              </ButtonRow>
            </>
          ) : (
            <i>{tPatientProfile("passwordGuide")}</i>
          )}

          {/* Passkeys */}
          <PasskeysSection>
            <h3 className="johnson-display">{tProfile("passkeys")}</h3>
            <i>{tProfile("passkeysGuide")}</i>
            <PasskeysList>
              {listPasskeysQuery.isLoading ? (
                <>
                  <div style={{ height: 52 }} />
                  <div style={{ height: 52 }} />
                </>
              ) : (
                (listPasskeysQuery.data ?? []).map((cred) => (
                  <PasskeyItem key={cred.CredentialId}>
                    <div className="passkey-info">
                      <Image
                        src={PasskeyIcon.src}
                        height={25}
                        width={25}
                        alt="passkeys"
                      />
                      <div>
                        <p className="title">
                          {cred.FriendlyCredentialName || tProfile("passkey")}
                        </p>
                        <p className="description">
                          {cred.CreatedAt
                            ? tProfile("passkeyAddedOn", {
                                date: new Date(
                                  cred.CreatedAt
                                ).toLocaleDateString(locale as string, {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                }),
                              })
                            : ""}
                        </p>
                      </div>
                    </div>
                    <PasskeyDeleteButton
                      type="button"
                      aria-label={tProfile("removePasskeyTitle")}
                      onClick={() =>
                        cred.CredentialId &&
                        handleDeletePasskey(cred.CredentialId)
                      }
                      disabled={deletePasskeyMutation.isPending}
                    >
                      <Image
                        src={TrashIcon.src}
                        height={16}
                        width={18}
                        alt="delete-passkey"
                      />
                    </PasskeyDeleteButton>
                  </PasskeyItem>
                ))
              )}
            </PasskeysList>

            <Button
              label={tProfile("addNewPasskey")}
              name="add-passkey"
              theme="tertiary"
              onPress={handleAddPasskey}
              isDisabled={!isPasskeySupported}
              icon={AddIcon.src}
              styles={{ width: "100%", borderRadius: 5 }}
            />
            {!isPasskeySupported && (
              <p style={{ fontSize: "0.75rem", marginTop: 5 }}>
                {tProfile("passkeysNotSupported")}
              </p>
            )}
          </PasskeysSection>
        </ProfileInfo>

        {/* Notification Preferences */}
        <ProfileInfo $disabled={isEditLogin || isEditProfile}>
          <div className="header-row">
            <div className="title">
              <Image
                src={NotifPrefIcon.src}
                height={25}
                width={25}
                alt="notif-info"
              />
              <h3>{tPatientProfile("notifications")}</h3>
            </div>
            {notificationsChanged && (
              <ButtonRow>
                <Button
                  label={tComponentText("cancel")}
                  name="cancel"
                  theme="tertiary"
                  onPress={handleCancelEditNotifs}
                  isDisabled={updatePatientPreferencesMutation.isPending}
                  small
                />
                <Button
                  label={tComponentText("saveNewPreferences")}
                  name="save-notif-pref"
                  theme="primary"
                  onPress={handleSaveNotifs}
                  isDisabled={updatePatientPreferencesMutation.isPending}
                  icon={
                    updatePatientPreferencesMutation.isPending
                      ? "loader"
                      : undefined
                  }
                  iconColor={
                    updatePatientPreferencesMutation.isPending
                      ? styledTheme.white
                      : undefined
                  }
                  small
                />
              </ButtonRow>
            )}
          </div>
          <i>{tPatientProfile("notifGuide")}</i>

          {/* Alerts */}
          <ProfileInfo
            style={{ borderRadius: 10, marginTop: 10 }}
            $disabled={updatePatientPreferencesMutation.isPending}
          >
            <div className="header-row">
              <div className="title">
                <Image
                  src={AlertPrefIcon.src}
                  height={25}
                  width={25}
                  alt="alert-info"
                />
                <h3>{tPatientProfile("alerts")}</h3>
              </div>
            </div>
            <SmsDisabledWarning>
              <span>{tComponentText("warning")}:</span>{" "}
              {tComponentText("smsWarning")}
            </SmsDisabledWarning>

            <NotifAlerts>
              {patientPreferencesQuery.isLoading
                ? Array.from({ length: 2 }).map((_, index) => (
                    <PreferenceItem key={index}>
                      <Skeleton width="200px" height="16px" />
                      <Skeleton width="60px" height="18px" />
                    </PreferenceItem>
                  ))
                : notificationCategories.map(({ key, label, description }) => {
                    const notificationSettings = editedPreferences?.[
                      key
                    ] as NotificationTypePreference;

                    return (
                      <PreferenceItem key={String(key)}>
                        <div>
                          <p className="title">{label}</p>
                          <p className="description">{description}</p>
                        </div>
                        <ToggleGroup>
                          <Button
                            icon={EmailIcon.src}
                            iconColor={styledTheme.white}
                            label={tComponentText("email")}
                            name="toggle-email"
                            theme={
                              notificationSettings?.email
                                ? "toggle-email"
                                : "toggle-off"
                            }
                            onPress={() =>
                              handleNotificationToggle(
                                key,
                                "email",
                                !notificationSettings?.email
                              )
                            }
                            isDisabled={isEditLogin || isEditProfile}
                            small
                          />
                          {canReceiveSms && (
                            <Button
                              icon={SmsIcon.src}
                              iconColor={styledTheme.white}
                              label={tComponentText("sms")}
                              name="toggle-sms"
                              theme={
                                notificationSettings?.sms
                                  ? "toggle-sms"
                                  : "toggle-off"
                              }
                              onPress={() =>
                                handleNotificationToggle(
                                  key,
                                  "sms",
                                  !notificationSettings?.sms
                                )
                              }
                              isDisabled={isEditLogin || isEditProfile}
                              small
                            />
                          )}
                        </ToggleGroup>
                      </PreferenceItem>
                    );
                  })}
            </NotifAlerts>
          </ProfileInfo>
        </ProfileInfo>
      </Profile>
      {passkeyToDelete && (
        <WarningModal
          toggleModal={() => setPasskeyToDelete(null)}
          modalWidth="450px"
          content={
            <DeletePasskeyContent
              onCancel={() => setPasskeyToDelete(null)}
              onConfirm={confirmDeletePasskey}
              isDeleting={deletePasskeyMutation.isPending}
            />
          }
        />
      )}
    </>
  );
}
